import {Injectable} from '@angular/core';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {HttpYarnTypesService} from '@infrastructure/http/yarn-type/http-yarn-types.service';
import {AgGridUtils, BaseComponent, ColDefBuilderFactoryService, LinkIconRendererComponent, StringUtils, Unit} from '@vdw/angular-component-library';
import {ColDef, ICellRendererParams, ITooltipParams, ValueGetterParams} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {map, Observable, ReplaySubject, takeUntil} from 'rxjs';
import {OverviewListYarnType} from './overview-list-yarn-type';

@Injectable()
export class OverviewListYarnTypeService extends BaseComponent {
  private pageUrls = LastModifiedCardUtils.getPageUrls('yarnType');

  public constructor(
    private readonly yarnTypes: HttpYarnTypesService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly l10nIntlService: L10nIntlService
  ) {
    super();
  }

  public getColumnDefsForListOfYarnTypes(possibleYarnTypesSubject: ReplaySubject<OverviewListYarnType[]>, canClickName: boolean): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('name', true)
        .withHeaderName('GENERAL.NAME')
        .withComparator(StringUtils.stringComparator)
        .withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
          return {
            params,
            canClick: canClickName,
            editPageUrl: this.pageUrls.edit
          };
        })
        .withTextMultiFilter(this.getPossibleColumnsValues('name'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('yarnTypeThreads.thread.rawMaterial.name')
        .withField('threads')
        .withHeaderName('TEXTILE_DATA.YARN_TYPE.RAW_MATERIAL')
        .withValueGetter((params: ValueGetterParams) => params.data.threads.map((thread: {rawMaterial: string}) => thread.rawMaterial).join(', '), true)
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('yarnTypeThreads.thread.rawMaterial.name'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('yarnCount')
        .withHeaderName('TEXTILE_DATA.YARN_TYPE.YARN_COUNT')
        .withRightAlignment()
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithUnit(params.getValue(), (params.node.data as OverviewListYarnType).yarnCountUnit, this.l10nIntlService))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, (params.node.data as OverviewListYarnType).yarnCountUnit, this.l10nIntlService))
        .withNumericMultiFilter(this.getPossibleColumnsValues('yarnCount'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('yarnCountInNm')
        .withHeaderName('TEXTILE_DATA.YARN_TYPE.YARN_COUNT', 1, false, 'GENERAL.UNIT.NUMBER_METRIC')
        .withRightAlignment()
        .withNumericMultiFilter(this.getPossibleColumnsValues('yarnCountInNm'))
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithUnit(params.getValue(), Unit.NUMBER_METRIC, this.l10nIntlService))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.NUMBER_METRIC, this.l10nIntlService))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('description', true)
        .withHeaderName('GENERAL.DESCRIPTION')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('description'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('threads')
        .withHeaderName('TEXTILE_DATA.YARN_TYPE.THREAD', 2, true)
        .withValueGetter((params: ValueGetterParams) => params.data.threads.length, true)
        .withNumericMultiFilter(
          possibleYarnTypesSubject.asObservable().pipe(
            map((listOfYarnTypes: OverviewListYarnType[]) => listOfYarnTypes.map((listOfYarnType: OverviewListYarnType) => listOfYarnType.threads.length)),
            takeUntil(this.unSubscribeOnViewDestroy)
          )
        )
        .withoutFilter()
        .withHide(true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('groupName', true)
        .withHeaderName('GENERAL.GROUP')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('groupName'))
        .withHide(true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('comment', true)
        .withHeaderName('GENERAL.COMMENT')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('comment'))
        .withHide(true)
        .build()
    ];
  }

  private getPossibleColumnsValues(columnIdentifier: string): Observable<string[]> {
    return this.yarnTypes.getPossibleValues(columnIdentifier).pipe(takeUntil(this.unSubscribeOnViewDestroy));
  }
}
